import { useCallback, useEffect, useLayoutEffect, useState, type MutableRefObject } from "react";
import { computeMenuPosition, type MenuPosition } from "../lib/menu-position";

/**
 * Fixed, viewport-clamped placement for a Select-style dropdown menu. Runs
 * before paint so the menu never renders at an absolute (container-clipped)
 * position first, and re-clamps on viewport changes (soft keyboard /
 * orientation) and whenever `recomputeKey` changes (menu content size).
 */
export function useMenuPosition(
  open: boolean,
  wrapRef: MutableRefObject<HTMLDivElement | null>,
  menuRef: MutableRefObject<HTMLDivElement | null>,
  recomputeKey: number,
): MenuPosition | null {
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null);

  const computePos = useCallback(() => {
    const wrap = wrapRef.current;
    const menu = menuRef.current;
    if (!wrap || !menu) return null;
    return computeMenuPosition(wrap.getBoundingClientRect(), menu.offsetHeight, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, [wrapRef, menuRef]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    setMenuPos(computePos());
  }, [open, computePos, recomputeKey]);

  useEffect(() => {
    if (!open) return;
    function onResize() {
      setMenuPos(computePos());
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, computePos]);

  return menuPos;
}
